import { FC, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import {
  GetOrganization200,
  GetOrganizationSettings200,
  UpdateOrganizationSettingsBody,
} from "~/api/model";
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  FONT_FAMILY_OPTIONS,
  type FontFamilyOptionKey,
  isFontFamilyOptionKey,
} from "~/features/settings/constants";
import logger from "~/utils/logger";

interface SettingsTypographyProps {
  updateSettings: (settings: UpdateOrganizationSettingsBody) => void;
  settings: GetOrganizationSettings200;
  organization: GetOrganization200;
}

const lineSpacings = [
  {
    value: "1",
    labelId: "settings.lineSpacingSingle",
    defaultMessage: "Dense",
    lineHeight: "1",
  },
  {
    value: "1.5",
    labelId: "settings.lineSpacingStandard",
    defaultMessage: "Regular",
    lineHeight: "1.5",
  },
  {
    value: "2",
    labelId: "settings.lineSpacingDouble",
    defaultMessage: "Relaxed",
    lineHeight: "2",
  },
];

const getFontFamilyOrDefault = (
  value: string | null | undefined
): FontFamilyOptionKey => {
  if (isFontFamilyOptionKey(value)) {
    return value;
  }

  return "sans";
};

export const SettingsTypography: FC<SettingsTypographyProps> = ({
  updateSettings,
  settings,
}) => {
  const [fontFamily, setFontFamily] = useState<FontFamilyOptionKey>(() => {
    return getFontFamilyOrDefault(settings?.fontFamily);
  });

  const [lineSpacing, setLineSpacing] = useState<string>(() => {
    return settings?.lineSpacing || "1.5";
  });

  useEffect(() => {
    if (isFontFamilyOptionKey(settings?.fontFamily)) {
      setFontFamily(settings.fontFamily);
    }
    if (settings?.lineSpacing) {
      setLineSpacing(settings.lineSpacing);
    }
  }, [settings?.fontFamily, settings?.lineSpacing]);

  const handleFontFamilyChange = (value: FontFamilyOptionKey) => {
    setFontFamily(value);

    try {
      updateSettings({ fontFamily: value });
    } catch (error) {
      logger.error("Failed to update font family:", error);
    }
  };

  const handleLineSpacingChange = (value: string) => {
    setLineSpacing(value);

    try {
      updateSettings({ lineSpacing: value });
    } catch (error) {
      logger.error("Failed to update line spacing:", error);
    }
  };

  return (
    <div className="w-full flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium">
          <FormattedMessage
            id="settings.fontFamily"
            defaultMessage={"Font Family"}
          />
        </span>
        <Select
          value={fontFamily}
          onValueChange={(value) =>
            handleFontFamilyChange(value as FontFamilyOptionKey)
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Select a font" />
          </SelectTrigger>
          <SelectContent>
            {FONT_FAMILY_OPTIONS.map(({ value, labelId, defaultMessage }) => (
              <SelectItem key={value} value={value}>
                <FormattedMessage
                  id={labelId}
                  defaultMessage={defaultMessage}
                />
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium">
          <FormattedMessage
            id="settings.lineSpacing"
            defaultMessage={"Line Spacing"}
          />
        </span>
        <RadioGroup value={lineSpacing}>
          {lineSpacings.map(({ value, labelId, defaultMessage }) => (
            <RadioGroupItem
              key={value}
              value={value}
              id={`line-spacing-${value}`}
              name="lineSpacing"
              checked={lineSpacing === value}
              onChange={(e) => handleLineSpacingChange(e.currentTarget.value)}
              label={
                <FormattedMessage
                  id={labelId}
                  defaultMessage={defaultMessage}
                />
              }
            />
          ))}
        </RadioGroup>
      </div>
    </div>
  );
};
